import React, { useEffect, useState } from 'react';
import { AnalysisRequirementUpdateInput } from 'services/candidate/types';
import { FormikErrors, useFormik } from 'formik';
import {
  useGetAnalysisRequirementsByCandidateBatchQuery,
  useUpdateAnalysisRequirementsMutation,
  useUpdateCandidateBatchGroupComponentsMutation
} from 'generated-graphql/hooks/apollo-hooks';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import { useDrawer } from 'services/drawer/hooks';
import { ToastType, useToast } from 'services/toast';
import { sortByDate } from 'utils/datetime';

import { useConfirmationModal } from 'services/modals/hooks';
import { findParentAnalysisRequirementId } from 'modules/analyse/utils/findParentAnalysisRequirement';
import { formatAnalysisRequirementsForSubmission } from 'modules/analyse/utils/formatAnalysisRequirementsForSubmission';
import { useTracking } from 'services/tracking/hooks';
import { handleRequirementsUpdateTracking } from 'modules/analyse/utils/handleRequirementsUpdateTracking';
import { AnalysisRequirementItem } from 'modules/analyse/types/AnaylsisRequirementItem';
import { Loading } from 'common/ui/Loading';
import { CandidateBatch } from 'generated-graphql/types';
import { RequirementsSettings } from './RequirementsSettings';
import { AutomationDrawerContainer } from './AutomationDrawerContainer';
import { FailedMessageWithHref } from 'common/ui';
import { requirementsValidationSchema } from 'modules/analyse/constants/requirementsValidationSchema';
import { AnalysisRequirementInputWithExtras } from 'modules/analyse/types/AnalysisRequirementInputWithExtras';
import { formatAnalysisRequirementItemToInputWithKey } from '../utils/formatAnalysisRequirementItemToInputWithKey';
import { removeKeyAndExtrasFromItems } from 'core/utils/reformatItemsWithKey';
import { AnalysisRequirementInput } from 'modules/analyse/types/AnalysisRequirementInput';

const minWidth = '750px';

type FormValues = {
  analysisRequirements: AnalysisRequirementInputWithExtras[];
  overwriteLockedCandidates: boolean;
};

type Props = {
  candidateBatch: CandidateBatch;
  setCandidateBatch: (candidateBatch: CandidateBatch) => void;
};

export const AnalysisRequirementsDrawer: React.FC<Props> = ({
  candidateBatch,
  setCandidateBatch
}) => {
  const { updateDrawerComponentProps, forceCloseDrawer, updateDrawerProps } =
    useDrawer();
  const { showToast } = useToast();
  const { showModal } = useConfirmationModal();
  const { track } = useTracking();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialAnalysisRequirements, setInitialAnalysisRequirements] =
    useState<AnalysisRequirementItem[]>();

  const [updateCandidateBatchGroupComponentsMutation] =
    useUpdateCandidateBatchGroupComponentsMutation();
  const [updateAnalysisRequirementsMutation] =
    useUpdateAnalysisRequirementsMutation();

  const candidateBatchId = candidateBatch.id;
  const candidateBatchGroupId = candidateBatch.candidateBatchGroupId;

  const submitForm = async ({
    requirementsToCreate,
    requirementsToDelete,
    requirementsToUpdate,
    overwriteLockedCandidates
  }: {
    requirementsToCreate: AnalysisRequirementInput[];
    requirementsToDelete: string[];
    requirementsToUpdate: AnalysisRequirementUpdateInput[];
    overwriteLockedCandidates: boolean;
  }) => {
    try {
      setIsSubmitting(true);

      await updateAnalysisRequirementsMutation({
        variables: {
          input: {
            candidateBatchId,
            requirementsToAdd: requirementsToCreate,
            requirementsToDelete,
            requirementsToUpdate,
            overwriteLockedCandidates
          }
        }
      });

      showToast({
        title: 'Requirements updated',
        type: ToastType.Success
      });
      forceCloseDrawer();
    } catch (error) {
      showToast({
        title: 'Something went wrong',
        message: (
          <FailedMessageWithHref
            failedMessage="Try updating the requirements again."
            emailSubject="Failed to update requirements"
          />
        ),
        type: ToastType.Error
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const detachCandidateBatchGroup = () => {
    setCandidateBatch({
      ...candidateBatch,
      candidateBatchGroupId: null
    });

    updateDrawerComponentProps({
      candidateBatch: {
        ...candidateBatch,
        candidateBatchGroupId: null
      }
    });
  };

  const getAnalysisRequirementParent = (id: string) => {
    const parentId = findParentAnalysisRequirementId(
      id,
      initialAnalysisRequirements ?? []
    );

    if (!parentId) {
      throw new Error('Parent id not found');
    }

    return parentId;
  };

  const submitFormForGroup = async ({
    requirementsToCreate,
    requirementsToDelete,
    requirementsToUpdate,
    candidateBatchGroupId,
    candidateBatchGroupName,
    overwriteLockedCandidates
  }: {
    requirementsToCreate: AnalysisRequirementInput[];
    requirementsToDelete: string[];
    requirementsToUpdate: AnalysisRequirementUpdateInput[];
    candidateBatchGroupId: string;
    overwriteLockedCandidates: boolean;
    candidateBatchGroupName: string | undefined | null;
  }) => {
    try {
      setIsSubmitting(true);

      await updateCandidateBatchGroupComponentsMutation({
        variables: {
          input: {
            candidateBatchGroupId,
            overwriteLockedCandidates,
            parentAnalysisRequirementsToAdd: requirementsToCreate,
            parentAnalysisRequirementsToDelete: requirementsToDelete.map((id) =>
              getAnalysisRequirementParent(id)
            ),
            parentAnalysisRequirementsToUpdate: requirementsToUpdate.map(
              (req) => {
                return {
                  ...req,
                  id: getAnalysisRequirementParent(req.id)
                };
              }
            )
          }
        }
      });

      showToast({
        title: `${candidateBatchGroupName || 'Your Ruleset'} has been updated`,
        type: ToastType.Success
      });

      forceCloseDrawer();
    } catch (e) {
      showToast({
        title: 'Something went wrong',
        message: (
          <FailedMessageWithHref
            failedMessage="Try updating the requirements again."
            emailSubject="Failed to update requirements"
          />
        ),
        type: ToastType.Error
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      analysisRequirements: [],
      overwriteLockedCandidates: false
    },
    validationSchema: requirementsValidationSchema,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: async (values) => {
      const { analysisRequirements, overwriteLockedCandidates } = values;

      const formattedWithoutKeysAndRequirementCount =
        removeKeyAndExtrasFromItems(analysisRequirements);

      const {
        requirementsToCreate,
        requirementsToDelete,
        requirementsToUpdate
      } = formatAnalysisRequirementsForSubmission(
        formattedWithoutKeysAndRequirementCount || [],
        initialAnalysisRequirements || []
      );

      handleRequirementsUpdateTracking({
        requirementsToDelete,
        requirementsToUpdate,
        requirementsToCreate,
        track
      });

      if (candidateBatchGroupId) {
        showModal({
          title: 'Save rules',
          message:
            'Do you want to save these settings for this analysis, or for all analyses that use this Ruleset?',
          confirmationText: 'Just this',
          showSecondaryButton: true,
          secondaryText: 'All',
          onConfirm: async () => {
            await submitForm({
              requirementsToCreate,
              requirementsToDelete,
              requirementsToUpdate,
              overwriteLockedCandidates
            });

            // Detach the candidateBatchGroupId from this analysis so on next save it will be saved only for this analysis
            detachCandidateBatchGroup();
          },
          onSecondaryClick: async () => {
            await submitFormForGroup({
              requirementsToCreate,
              requirementsToDelete,
              requirementsToUpdate,
              candidateBatchGroupId,
              candidateBatchGroupName: candidateBatch.candidateBatchGroup?.name,
              overwriteLockedCandidates
            });
          }
        });

        return;
      }

      await submitForm({
        requirementsToCreate,
        requirementsToDelete,
        requirementsToUpdate,
        overwriteLockedCandidates
      });
    }
  });

  const { loading } = useGetAnalysisRequirementsByCandidateBatchQuery({
    variables: {
      candidateBatchId
    },
    onCompleted: (data) => {
      const result = extractQueryListItems(data);

      const sortedResult =
        result?.sort((a, b) => sortByDate(a.createdAt, b.createdAt, 'asc')) ??
        [];

      setInitialAnalysisRequirements(sortedResult);
      formik.setFieldValue(
        'analysisRequirements',
        formatAnalysisRequirementItemToInputWithKey(sortedResult)
      );
    }
  });

  useEffect(() => {
    const { requirementsToCreate, requirementsToDelete, requirementsToUpdate } =
      formatAnalysisRequirementsForSubmission(
        formik.values.analysisRequirements || [],
        initialAnalysisRequirements || []
      );

    if (
      requirementsToCreate.length > 0 ||
      requirementsToDelete.length > 0 ||
      requirementsToUpdate.length > 0 ||
      formik.values.overwriteLockedCandidates
    ) {
      updateDrawerProps({
        preventClose: true,
        confirmationModalProps: {
          title: 'Save changes?',
          confirmationText: 'Yes',
          secondaryText: 'No',
          cancelText: 'Cancel',
          onConfirm: () => {
            formik.submitForm();
            updateDrawerProps({ preventClose: false });
          }
        }
      });
    } else {
      updateDrawerProps({
        preventClose: false
      });
    }
  }, [formik.values, initialAnalysisRequirements]);

  if (loading) {
    return (
      <Loading
        containerSx={{
          minWidth
        }}
      />
    );
  }

  return (
    <AutomationDrawerContainer
      title="Requirements"
      subtitle="Adjust the weighting to re-analyse your applicant’s performance."
      candidateBatch={candidateBatch}
      loading={loading}
      isSubmitting={isSubmitting}
      onSubmit={formik.submitForm}
      onCancel={forceCloseDrawer}
      handleOverrideChecked={(value) => {
        formik.setFieldValue('overwriteLockedCandidates', value);
      }}
      overrideCheckedValue={formik.values.overwriteLockedCandidates}
      updateButtonLabel="Re-analyse applicants"
    >
      <RequirementsSettings
        setFieldValue={formik.setFieldValue}
        setFieldError={formik.setFieldError}
        candidateBatchCandidateCount={candidateBatch.candidatesCount}
        value={formik.values.analysisRequirements}
        errors={
          formik.errors.analysisRequirements
            ? (formik.errors
                .analysisRequirements as FormikErrors<AnalysisRequirementInputWithExtras>[])
            : undefined
        }
      />
    </AutomationDrawerContainer>
  );
};
